/**
 * `ChannelPane` — the Telegram/WhatsApp tab body on the Agent page. A read-only
 * messenger that LOOKS AND FEELS like the real web client (operator direction
 * 2026-07-22): a chat list that opens a conversation of tailed bubbles over a
 * tinted wallpaper. Email never mounts this (it stays a placeholder until
 * `mail.*` / C3 ships).
 *
 * ## Layout (measured on the PANE, not the viewport — it is user-resizable
 * 35–65vw, so a viewport breakpoint would lie):
 *   - `split` (≥ 720px) → chat list beside the open conversation (desktop
 *                         Telegram/WhatsApp); the first conversation auto-opens;
 *   - `single`          → one column that swaps list ⇄ conversation with a back
 *                         button (the narrow/mobile messenger shape).
 *
 * ## State machine (first match wins) — availability is decided from `status` +
 * `hasActivity` ONLY, NEVER from inventory row presence: an absent row is
 * treated identically to `disconnected`, which is what makes the same code
 * correct against today's stale galvão image (asymmetric two-row inventory) and
 * tomorrow's fixed total inventory (`5fde09e7`).
 *
 *   0. gate loading                    → spinner
 *   1. gate disabled (no channels.*)   → placeholder ("Em breve…")
 *   2. status ≠ connected / row absent → the Connect CTA (`AgentChannelConnectCta`)
 *      — and the branch is HELD while that CTA's dialog is open (I-4), so a
 *      pairing that flips `status` mid-flow cannot evict the lawyer's wizard
 *   3. connected & !hasActivity        → zero-state ("nada ainda…")
 *   4. connected & hasActivity         → chat list ⇄ conversation
 *
 * I-15 (2026-09-11, `ai-docs/lexide-agent-tab-connect-continuity-miniplan.md`):
 * a CONFIGURED row whose endpoint 1 is not live and whose `/details` reports
 * `requiresRoll: true` is a token SAVED BUT NOT ACTIVATED — the bot is deaf.
 * That renders `ChannelStep1Notice` in its `savedNotActive` variant instead of
 * the tab silently showing old conversations. An omitted `requiresRoll` is
 * UNKNOWN and renders exactly as before.
 *
 * S10 1b (2026-09-11, `ai-docs/lexide-channelpane-step1-notice-miniplan.md`):
 * states 3–4 with endpoint 1 live but endpoint 2 not — `imBadge` → "step1",
 * `ai-docs/lexiun-user-setup.md` §2-bis — render `ChannelStep1Notice` ABOVE
 * the view: "Etapa 1 de 2 concluída — falta a etapa 2…" plus
 * [Concluir a etapa 2], which opens the connect dialog. An unread probe
 * renders 3–4 alone.
 *
 * P0 STATUS-FIRST (`ai-docs/lexide-agent-im-tabs-options-plan.md` §5 P0): a
 * not-connected channel reports THAT before it reports "no activity" — an
 * account surface, not an append-only log.
 *
 * See `ai-docs/lexide-agent-channel-tabs-impl-plan.md` §5.1.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { imChannelState } from "@/lib/channel-endpoint-state";
import { useChannelsInventory } from "@/hooks/useChannels";
import { useContactPeerNames } from "@/hooks/useContactPeerNames";
import { useChannelEndpoints } from "@/hooks/useChannelEndpoints";
import { useChannelActivation } from "@/hooks/useChannelActivation";
import type { ChannelConversation } from "@/api/lx-channels";
import { AgentChannelPlaceholder } from "@/components/agent/AgentChannelPlaceholder";
import { AgentChannelConnectCta } from "@/components/agent/AgentChannelConnectCta";
import { ChannelStep1Notice } from "@/components/agent/ChannelStep1Notice";
import { ChannelConnectDialog } from "@/components/contacts/ChannelConnectDialog";
import { ChannelConversationList } from "@/components/agent/ChannelConversationList";
import { ChannelThread } from "@/components/agent/ChannelThread";
import { getMessengerTheme } from "@/components/agent/messenger-theme";

/** At/above this pane width the list sits beside the conversation. */
const TWO_COLUMN_MIN_PX = 720;

export function ChannelPane({
  channel,
  icon,
  fill = false,
  onGoToTelegram,
}: {
  channel: "telegram" | "whatsapp";
  icon: LucideIcon;
  /** Fill the host's height (focus pane) instead of the page's boxed height. */
  fill?: boolean;
  /** WhatsApp only: the not-connected CTA's Telegram-first nudge target. */
  onGoToTelegram?: () => void;
}) {
  const { t } = useTranslation();
  const { channels, gate, refetch } = useChannelsInventory();
  // Resolve peer → curated contact identity ONCE here (single `contacts.list`
  // round-trip) and thread it to both children. Degrades to `undefined` on any
  // failure — names/avatars are a nicety, never a dependency.
  const { resolve: resolvePeerName } = useContactPeerNames();
  const [selected, setSelected] = useState<ChannelConversation | null>(null);
  const theme = getMessengerTheme(channel);

  // Container-aware layout: measure OUR width, not the viewport (the same lesson
  // FocusPaneHost + EmailPane learned — the pane is user-resizable).
  //
  // A CALLBACK ref, not an effect keyed on `gate`: the observed element only
  // exists in the final return, so every state before it (placeholder,
  // zero-state) leaves the ref null. A channel that reaches the list view
  // WITHOUT `gate` changing — precisely what happens when a freshly paired
  // WhatsApp flips `status` to connected — would never be measured, and the tab
  // would open stuck in single-column. Attaching the observer when the node
  // itself appears has no such seam.
  const roRef = useRef<ResizeObserver | null>(null);
  const [width, setWidth] = useState(0);
  const setRoot = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    roRef.current = ro;
  }, []);
  useEffect(() => () => roRef.current?.disconnect(), []);
  const twoColumn = width >= TWO_COLUMN_MIN_PX;

  // Availability comes from the inventory row (status-first, below); the live
  // endpoint probe only decides whether a CONNECTED row is the lawyer's yet
  // (§2-bis). Both are read here, before the early returns (rules of hooks).
  const row = channels.find((c) => c.id === channel);
  const hasActivity = row?.hasActivity ?? false;
  // Absent row ≡ disconnected — never reason about presence (5fde09e7).
  const status = row?.status ?? "disconnected";
  const [connectOpen, setConnectOpen] = useState(false);
  // I-4: the CTA owns its dialog but this pane owns the BRANCH it lives in,
  // and a status flip mid-pairing would swap that branch and evict the open
  // wizard. Mirroring its open state (ide's additive `onOpenChange`,
  // `e1e0bfa4f`) lets the branch survive until the lawyer closes it.
  const [ctaOpen, setCtaOpen] = useState(false);
  // Probed only for a connected row on a live gate, and paused while the
  // connect dialog (which runs its own probe) is open — re-enabling re-reads
  // at once, since the hook's read effect keys on `enabled`. 10s rather than
  // the dialog's 3s: the assistant card polls the same RPCs on this page.
  const endpoints = useChannelEndpoints(channel, {
    enabled: gate === "enabled" && status === "connected" && !connectOpen,
    pollMs: 10_000,
  });
  // Only a DEFINITE endpoint-1-yes / endpoint-2-no is step 1; an unread probe
  // is `unknown` and renders the view alone — never either claim.
  // RETEST-1 I-15 (operator-decided 2026-09-11). A row the inventory calls
  // `connected` only means CONFIGURED — "a bare creds file resolves to
  // connected" (`api/lx-channels.ts:36-43`) — so a STORED token keeps this pane
  // on the connected view while endpoint 1 reads NOT live and the card's badge
  // reads `disconnected` (the nothing-set-up chip). That pair is two different
  // states: "saved, not activated yet" and "was live, now down". Only
  // `/details` `requiresRoll` separates them, and `null` (unread, failed, or a
  // runtime that omits the field) separates neither — so it claims nothing.
  // `ep1 === false` is part of the GATE, not just the verdict: `savedNotActive`
  // can only arise when endpoint 1 reads down, so a healthy connected row has no
  // reason to poll `/details` at all. Without it every working channel polls
  // forever for a value it can never use — the same defect `ide` hit on the card
  // and fixed the same way (their note via `tester-galvao`, 2026-09-11).
  const activation = useChannelActivation(channel, {
    enabled:
      gate === "enabled" &&
      status === "connected" &&
      endpoints.ep1 === false &&
      !connectOpen &&
      !ctaOpen,
    pollMs: 10_000,
  });
  // ONE helper answers for both surfaces (`imChannelState`, ide's `e1e0bfa4f`),
  // so the card and this tab cannot drift apart. It composes `imBadge` and adds
  // `savedNotActive` only for `ep1 === false && requiresRoll === true`, which is
  // why `requiresRoll === null` can never reach it.
  const channelState = imChannelState(endpoints.ep1, endpoints.ep2, activation.requiresRoll);
  const step1 = channelState === "step1";
  // `channel === "telegram"` restates the hook's scope (`requiresRoll` exists on
  // no other channel's details) so the invariant is local and testable, and
  // `status` keeps the inventory half, which the helper never sees.
  const savedNotActive =
    channel === "telegram" && status === "connected" && channelState === "savedNotActive";
  // Mutually exclusive by construction: the helper returns one state.
  const notice: "step1" | "savedNotActive" | null = step1
    ? "step1"
    : savedNotActive
      ? "savedNotActive"
      : null;

  // The S10 1b notice sits ABOVE whichever connected view renders, and its
  // action opens the SAME connect dialog the card's "Etapa 1 de 2" badge
  // drives — the dialog's live flow lands on the pairing panel.
  const withNotice = (view: ReactElement): ReactElement =>
    notice || connectOpen ? (
      <div
        // Testid kept at `-step1-` though it now hosts BOTH notices: it is an
        // existing selector and renaming it would break instruments that
        // already target it. The variant is told apart by the notice's own
        // testid (`channel-step1-notice` / `channel-saved-not-active-notice`).
        data-testid={`agent-channel-step1-${channel}`}
        className={cn("flex min-h-0 flex-col gap-3", fill && "h-full flex-1")}
      >
        {notice && (
          <ChannelStep1Notice
            variant={notice}
            // Only `step1` has an action. `savedNotActive` deliberately offers
            // none (activation-without-a-roll has not shipped), so it is handed
            // no handler rather than one it would silently drop.
            onContinue={notice === "step1" ? () => setConnectOpen(true) : undefined}
          />
        )}
        {view}
        {connectOpen && (
          <ChannelConnectDialog
            channel={channel}
            onClose={() => {
              setConnectOpen(false);
              refetch();
            }}
          />
        )}
      </div>
    ) : (
      view
    );

  if (gate === "loading") {
    return (
      <div className="flex items-center justify-center py-16 text-cream-500">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  // A runtime whose image predates `channels.*` — keep the honest placeholder.
  if (gate === "disabled") {
    return <AgentChannelPlaceholder icon={icon} channelKey={channel} />;
  }

  // Status-first (P0): a not-connected channel reports that BEFORE "no
  // activity". An absent row folds to `disconnected` above, so it also lands
  // here. Only a channel that IS connected proceeds to the zero-state / list.
  // `|| ctaOpen`: I-4. Pairing writes `status` connected the moment endpoint 1
  // closes; without this the branch swaps mid-flow and the open wizard is
  // destroyed — evicted from the tree, not broken inside.
  if (status !== "connected" || ctaOpen) {
    // The tab hosts CONNECT directly rather than dead-ending to Settings
    // (operator direction 2026-07-25/26). Both channels ChannelPane serves —
    // WhatsApp AND Telegram — have a full `ChannelConnectDialog` flow (endpoint 1
    // + owner-pairing endpoint 2), so both get the tab CONNECT button. A freshly
    // paired channel re-reads via `refetch` when the dialog closes.
    // Telegram's side-1 now hosts the bot-token paste INLINE (the `TelegramBotSidePanel`
    // launches `TelegramSetupWizard` right in the dialog — decoupled from
    // `useOpenclawSettings` via `useOpenclawSettingsOptional` + an injected
    // `onApplied`), so it no longer sends the lawyer to Settings. (Email is not
    // served here — its connect is the backend-pending E0 shell; EmailPane keeps
    // the honest placeholder.)
    return (
      <AgentChannelConnectCta
        channel={channel}
        icon={icon}
        onClose={refetch}
        onOpenChange={setCtaOpen}
        onGoToTelegram={onGoToTelegram}
      />
    );
  }

  if (!hasActivity) {
    return withNotice(
      <AgentChannelPlaceholder
        icon={icon}
        channelKey={channel}
        titleKey="agent.channels.zeroState.title"
        bodyKey="agent.channels.zeroState.body"
        testId="channel-zero-state"
      />,
    );
  }

  const list = (
    <ChannelConversationList
      channel={channel}
      onSelect={setSelected}
      resolvePeerName={resolvePeerName}
      selectedPeer={twoColumn ? (selected ? selected.peer : undefined) : undefined}
      autoSelectFirst={twoColumn}
    />
  );

  const thread = selected ? (
    <ChannelThread
      channel={channel}
      conversation={selected}
      onBack={() => setSelected(null)}
      resolvePeerName={resolvePeerName}
      showBack={!twoColumn}
    />
  ) : null;

  return withNotice(
    <div
      ref={setRoot}
      data-testid={`agent-channel-pane-${channel}`}
      data-layout={twoColumn ? "split" : "single"}
      className={cn("flex min-h-0 overflow-hidden", fill ? "h-full flex-1" : "h-[32rem]")}
    >
      {twoColumn ? (
        <>
          <div
            className={cn(
              // The list owns its own scrolling so its search field stays put.
              "flex min-h-0 w-[19rem] shrink-0 flex-col overflow-hidden border-r",
              theme.panelBorderClass,
              theme.listPanelClass,
            )}
          >
            {list}
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {thread ?? (
              // WhatsApp shows its wallpaper behind the "pick a chat" prompt;
              // Telegram keeps the neutral panel it shipped with (per-channel,
              // so this stays a WhatsApp change only).
              <div
                className={cn(
                  "flex h-full items-center justify-center px-6 text-center text-caption text-cream-500",
                  theme.emptyPaneClass,
                )}
              >
                {t("agent.channels.selectConversation")}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {thread ?? <div className="min-h-0 flex-1 overflow-hidden">{list}</div>}
        </div>
      )}
    </div>,
  );
}
